package fr.frux.core.designsystem.icons

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.addPathNodes
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.unit.dp

/**
 * Species glyphs used as map marker sprites (SDF) and as tinted fallback icons on web.
 *
 * ## Stroked line icons, and what that cost to establish
 * These glyphs are uploaded to MapLibre's `ImageManager` as **signed-distance-field**
 * (SDF) sprites (maplibre-compose 0.13.1, see ADR 0007): the marker layer rasterizes
 * each [ImageVector] to a monochrome bitmap and uploads it with `isSdf = true`, then
 * lets the GPU tint/scale it per-feature at render time. Anything thinner than the SDF's
 * spread radius gets eaten by the distance transform, which is why every glyph here used
 * to be a solid silhouette with no stroke at all.
 *
 * That rule was lifted deliberately, not forgotten. The pastille marker redesign moved
 * these glyphs to the Lucide line style; a 2-unit stroke on Lucide's 24-unit viewport is
 * three times the old 2/36 floor, and it was checked on screen before the whole set was
 * converted — Android (real SDF pipeline) and web (js, plain tinted `Image`, no SDF) both
 * render it cleanly at the ~20dp the markers draw at. Go thinner than 2 units, or below
 * that on-screen size, and the distance transform starts eating the line again.
 *
 * Rules followed by every glyph below:
 * - Lucide geometry: 24x24 viewport, 2-unit stroke, round caps and joins, no fill.
 * - Paths are pasted verbatim from `lucide-static` and parsed by `addPathNodes` — never
 *   retraced by hand, so nothing drifts from the source drawing.
 * - Glyphs Lucide has no icon for (poire, prune, figue, noisette, chataigne, mure,
 *   grappe-baies, ombelle, and every glyph added for the species-per-fruit set: coing,
 *   nefle, peche, abricot, olive, kaki, grenade...) are drawn here in the same geometry,
 *   and must stay legible at 34px — the marker's real size.
 * - One glyph per fruit, not per genus: hors saison the pastille turns grey and the glyph
 *   is the only cue left, so a pêcher, a prunier and a griottier must not share a drawing.
 *   Deliberate exceptions (same drawing, different species): noyer/noyer noir, mûrier/ronce,
 *   cassis/aubépine on grappe-baies, courgette/potiron on courge.
 *
 * Icons from Lucide (https://lucide.dev), lucide-static 0.462.0, ISC License.
 *
 * ## Key contract with feature-map
 * [all] is keyed by the stable `speciesGlyphKey` string feature-map resolves per tree
 * (35 species glyphs + 5 generic fallbacks keyed by harvested part, used when a species
 * has no dedicated glyph, plus 2 site plaque glyphs). The resolver lives next door in
 * `SpeciesGlyphResolver.kt`; feature-map delegates to it. These string keys are
 * the contract; renaming one is a breaking change for the marker layer.
 */
object FruxSpeciesGlyphs {
    const val KEY_POMME = "pomme"
    const val KEY_POIRE = "poire"
    const val KEY_COING = "coing"
    const val KEY_NEFLE = "nefle"
    const val KEY_SORBE = "sorbe"
    const val KEY_NEFLE_JAPON = "nefle-japon"
    const val KEY_CERISE = "cerise"
    const val KEY_PECHE = "peche"
    const val KEY_ABRICOT = "abricot"
    const val KEY_PRUNE = "prune"
    const val KEY_PETITES_PRUNES = "petites-prunes"
    const val KEY_PRUNELLE = "prunelle"
    const val KEY_AMANDE = "amande"
    const val KEY_FIGUE = "figue"
    const val KEY_OLIVE = "olive"
    const val KEY_KAKI = "kaki"
    const val KEY_GRENADE = "grenade"
    const val KEY_ARBOUSE = "arbouse"
    const val KEY_NOIX = "noix"
    const val KEY_NOISETTE = "noisette"
    const val KEY_CHATAIGNE = "chataigne"
    const val KEY_MURE = "mure"
    const val KEY_FRAMBOISE = "framboise"
    const val KEY_GROSEILLE = "groseille"
    const val KEY_GRAPPE_BAIES = "grappe-baies"
    const val KEY_MYRTILLE = "myrtille"
    const val KEY_CYNORHODON = "cynorhodon"
    const val KEY_OMBELLE = "ombelle"
    const val KEY_RAISIN = "raisin"
    const val KEY_FRAISE = "fraise"
    const val KEY_BRIN = "brin"
    const val KEY_RADIS = "radis"
    const val KEY_COURGE = "courge"
    const val KEY_SALADE = "salade"
    const val KEY_GOUSSE = "gousse"

    // Fallbacks keyed by harvested part (species_harvest, ADR 0006), used when a
    // species has no dedicated glyph above.
    const val KEY_FRUIT = "fruit"
    const val KEY_FLEUR = "fleur"
    const val KEY_FEUILLE = "feuille"
    const val KEY_COQUE = "coque"
    const val KEY_RACINE = "racine"

    // Glyphes des plaques de lieu de cueillette : un lieu ouvert, un lieu verrouille. Hors du
    // resolveur d'especes, aucune espece ne doit y tomber.
    const val KEY_ARBRE = "arbre"
    const val KEY_CADENAS = "cadenas"

    private val GlyphFill = SolidColor(Color.Black)

    /**
     * Glyphe trace en trait, a la geometrie Lucide (lucide-static 0.462.0, ISC).
     *
     * Le `d` SVG est colle verbatim et parse par `addPathNodes` : rien n'est retranscrit a la main,
     * donc rien ne derive du dessin d'origine. Le viewport reste celui de Lucide (24), pas celui des
     * silhouettes maison (36) — c'est le meme rapport une fois mis a l'echelle par le rendu.
     *
     * `GlyphFill` sert de couleur de trait et non de remplissage : le nom vient de l'epoque des
     * silhouettes, la couleur elle-meme n'a jamais compte — MapLibre la remplace par `iconColor`, et
     * le chemin web par un `ColorFilter`.
     */
    private fun lucideGlyph(
        name: String,
        vararg pathData: String,
    ): ImageVector =
        ImageVector
            .Builder(
                name = name,
                defaultWidth = 36.dp,
                defaultHeight = 36.dp,
                viewportWidth = LUCIDE_VIEWPORT,
                viewportHeight = LUCIDE_VIEWPORT,
            ).apply {
                pathData.forEach { data ->
                    addPath(
                        pathData = addPathNodes(data),
                        stroke = GlyphFill,
                        strokeLineWidth = LUCIDE_STROKE_WIDTH,
                        strokeLineCap = StrokeCap.Round,
                        strokeLineJoin = StrokeJoin.Round,
                    )
                }
            }.build()

    /**
     * Cercle sous forme de chaine de path SVG : `addPathNodes` ne parse que des paths, la ou Lucide
     * decrit certaines icones avec des `<circle>`. Deux arcs d'un demi-tour, comme le fait le SVG
     * lui-meme quand on convertit un cercle.
     */
    private fun circlePath(
        cx: Float,
        cy: Float,
        r: Float,
    ): String = "M${cx - r} ${cy}a$r $r 0 1 0 ${r * 2} 0a$r $r 0 1 0 ${-r * 2} 0Z"

    private const val LUCIDE_VIEWPORT = 24f
    private const val LUCIDE_STROKE_WIDTH = 2f

    val Pomme: ImageVector by lazy {
        lucideGlyph(
            "GlyphPomme",
            "M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 " +
                "1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 " +
                "4-1.06Z",
            "M10 2c1 .5 2 2 2 5",
        )
    }

    val Poire: ImageVector by lazy {
        lucideGlyph(
            "GlyphPoire",
            "M12 22c-3.6 0-6-2.6-6-5.9 0-2.3 1.3-3.8 2.2-5.1.8-1.2 1.3-2.2 1.3-3.6C9.5 5.5 10.6 " +
                "4 12 4s2.5 1.5 2.5 3.4c0 1.4.5 2.4 1.3 3.6.9 1.3 2.2 2.8 2.2 5.1 0 3.3-2.4 " +
                "5.9-6 5.9Z",
            "M12 4V2",
            "M12.5 2.8c1.2-.6 2.4-.5 3.3 0",
        )
    }

    val Cerise: ImageVector by lazy {
        lucideGlyph(
            "GlyphCerise",
            "M2 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3-2.5-2-5 .24-5 3Z",
            "M12 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3-2.5-2-5 .24-5 3Z",
            "M7 14c3.22-2.91 4.29-8.75 5-12 1.66 2.38 4.94 9 5 12",
            "M22 9c-4.29 0-7.14-2.33-10-7 5.71 0 10 4.67 10 7Z",
        )
    }

    /** Prune ovale, allongee verticalement : la pêche et l'abricot sont ronds, c'est ce qui les distingue en petit. */
    val Prune: ImageVector by lazy {
        lucideGlyph(
            "GlyphPrune",
            "M12 21.5c-3.5 0-6-3.4-6-7.6S8.5 6 12 6s6 3.7 6 7.9-2.5 7.6-6 7.6Z",
            "M12 6.4c-1.5 2.8-1.5 11.6 0 14.6",
            "M12 6c0-1.8 1-3.2 2.6-3.8",
        )
    }

    val Figue: ImageVector by lazy {
        lucideGlyph(
            "GlyphFigue",
            "M12 21.8c-4 0-6.6-2.8-6.6-6.5 0-3 1.7-5.4 3.8-6.8.9-.6 1.4-1.5 1.5-2.5h2.6c.1 1 .6 " +
                "1.9 1.5 2.5 2.1 1.4 3.8 3.8 3.8 6.8 0 3.7-2.6 6.5-6.6 6.5Z",
            "M12 6V3.2",
            "M12.5 3.8c1.3-1.1 2.9-1.3 4.2-.9",
        )
    }

    val Noix: ImageVector by lazy { nutGlyph("GlyphNoix") }

    val Noisette: ImageVector by lazy {
        lucideGlyph(
            "GlyphNoisette",
            "M12 21.5a5.8 5.8 0 1 1 0-11.6 5.8 5.8 0 0 1 0 11.6Z",
            "M6.4 12.2C7.1 8.5 9.2 6.4 12 6.4s4.9 2.1 5.6 5.8",
            "M12 6.4V3.2",
        )
    }

    val Chataigne: ImageVector by lazy {
        lucideGlyph(
            "GlyphChataigne",
            "M12 20.5a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13Z",
            "M12 7.5V3.6",
            "m6.8 9.2-2.6-2.6",
            "m17.2 9.2 2.6-2.6",
            "M5.5 14H2.2",
            "M18.5 14h3.3",
            "M12 14v3.5",
        )
    }

    val Mure: ImageVector by lazy {
        lucideGlyph(
            "GlyphMure",
            circlePath(9.6f, 9.4f, 2.3f),
            circlePath(14.4f, 9.4f, 2.3f),
            circlePath(7.2f, 13.5f, 2.3f),
            circlePath(12f, 13.5f, 2.3f),
            circlePath(16.8f, 13.5f, 2.3f),
            circlePath(9.6f, 17.6f, 2.3f),
            circlePath(14.4f, 17.6f, 2.3f),
            "M12 6.9V3.6",
            "M12.4 4.6c1.2-1 2.6-1.2 3.8-.8",
        )
    }

    val GrappeBaies: ImageVector by lazy {
        lucideGlyph(
            "GlyphGrappeBaies",
            "M12 3.6v6.6",
            "M9.4 11.4 12 10.2l2.6 1.2",
            circlePath(9.2f, 13.6f, 2.9f),
            circlePath(14.8f, 13.6f, 2.9f),
            circlePath(12f, 18.2f, 2.9f),
        )
    }

    val Ombelle: ImageVector by lazy {
        lucideGlyph(
            "GlyphOmbelle",
            "M12 21.6v-8.4",
            "M12 13.2 6.2 8.4",
            "M12 13.2 17.8 8.4",
            "M12 13.2V6.4",
            "M12 13.2 8.6 6.9",
            "M12 13.2l3.4-6.3",
            circlePath(6.2f, 7.6f, 1.5f),
            circlePath(17.8f, 7.6f, 1.5f),
            circlePath(12f, 5.2f, 1.5f),
            circlePath(8.6f, 6.1f, 1.5f),
            circlePath(15.4f, 6.1f, 1.5f),
        )
    }

    val Raisin: ImageVector by lazy {
        lucideGlyph(
            "GlyphRaisin",
            "M22 5V2l-5.89 5.89",
            circlePath(16.6f, 15.89f, 3f),
            circlePath(8.11f, 7.4f, 3f),
            circlePath(12.35f, 11.65f, 3f),
            circlePath(13.91f, 5.85f, 3f),
            circlePath(18.15f, 10.09f, 3f),
            circlePath(6.56f, 13.2f, 3f),
            circlePath(10.8f, 17.44f, 3f),
            circlePath(5f, 19f, 3f),
        )
    }

    val Coing: ImageVector by lazy {
        lucideGlyph(
            "GlyphCoing",
            "M12 21.5c-4 0-7-2.6-7-6.3 0-1.5.5-2.6 1.2-3.6-.3-1.9.8-3.6 2.7-4 1-.9 2-1.3 3.1-1.3s2.1.4 3.1 " +
                "1.3c1.9.4 3 2.1 2.7 4 .7 1 1.2 2.1 1.2 3.6 0 3.7-3 6.3-7 6.3Z",
            "M12 6.3V3.5",
            "M12 4.6c1.6-1.6 3.8-2 5.8-1.2-1 1.9-3.4 2.6-5.8 1.2Z",
            "M9 16h.01",
            "M15 16h.01",
            "M12 18.5h.01",
        )
    }

    val Nefle: ImageVector by lazy {
        lucideGlyph(
            "GlyphNefle",
            circlePath(12f, 15f, 6.5f),
            "M12 8.5 9.5 3.5",
            "M12 8.5l2.5-5",
            "M12 8.5 6.5 5.5",
            "M12 8.5l5.5-3",
        )
    }

    val Sorbe: ImageVector by lazy {
        lucideGlyph(
            "GlyphSorbe",
            "M7.5 21.5a3.5 3.5 0 0 1-3.5-3.5c0-2 1.6-3 2.3-4.5.3-.6.7-1 1.2-1s.9.4 1.2 1c.7 1.5 2.3 2.5 2.3 " +
                "4.5a3.5 3.5 0 0 1-3.5 3.5Z",
            "M16.5 21.5a3.5 3.5 0 0 1-3.5-3.5c0-2 1.6-3 2.3-4.5.3-.6.7-1 1.2-1s.9.4 1.2 1c.7 1.5 2.3 2.5 2.3 " +
                "4.5a3.5 3.5 0 0 1-3.5 3.5Z",
            "M7.5 12.5 12 4l4.5 8.5",
            "M12 4V2",
        )
    }

    val NefleJapon: ImageVector by lazy {
        lucideGlyph(
            "GlyphNefleJapon",
            "M8.5 21.5c-2.5 0-4.5-2.6-4.5-5.7S6 10 8.5 10s4.5 2.7 4.5 5.8-2 5.7-4.5 5.7Z",
            "M8.5 10c0-2.2 1.3-4 3.5-4.6",
            "M12 9.5c.8-4 3.8-6.8 9-7.5-.5 4.6-4 7.4-9 7.5Z",
            "M12 9.5l5-4.5",
        )
    }

    val Peche: ImageVector by lazy {
        lucideGlyph(
            "GlyphPeche",
            "M12 21.5c-4.4 0-7.5-3.2-7.5-7.2 0-3.9 3-6.8 7.5-6.8s7.5 2.9 7.5 6.8c0 4-3.1 7.2-7.5 7.2Z",
            "M12 7.6c-2.2 3-2.2 10.4 0 13.8",
            "M12 7.5c.6-2.8 2.9-4.8 6.5-4.7-.5 3.2-3 5-6.5 4.7Z",
        )
    }

    val Abricot: ImageVector by lazy {
        lucideGlyph(
            "GlyphAbricot",
            circlePath(12f, 14f, 7f),
            "M12 17.6c-1.7 0-2.7-1.6-2.7-3.6s1-3.6 2.7-3.6 2.7 1.6 2.7 3.6-1 3.6-2.7 3.6Z",
            "M12 7V3.5",
        )
    }

    val PetitesPrunes: ImageVector by lazy {
        lucideGlyph(
            "GlyphPetitesPrunes",
            "M2.5 3.5c6 .8 13 .3 19-.8",
            "M5.5 3.8v3",
            "M12 4v5",
            "M18.5 3.2v3",
            circlePath(5.5f, 10f, 3f),
            circlePath(12f, 12f, 3f),
            circlePath(18.5f, 9.5f, 3f),
        )
    }

    val Prunelle: ImageVector by lazy {
        lucideGlyph(
            "GlyphPrunelle",
            "M3 20.5 20.5 3",
            "M8 15.5 5.5 13",
            "M13 10.5 10.5 8",
            "M17.5 6 15.5 4",
            "M11 12.6l1.6 1.8",
            circlePath(14.3f, 16.6f, 3f),
            "M15.8 7.8l1.4 1.6",
            circlePath(18.8f, 11.8f, 2.4f),
        )
    }

    val Amande: ImageVector by lazy {
        lucideGlyph(
            "GlyphAmande",
            "M12 2.5c3.8 3.4 6 7.6 6 11.4 0 4.4-2.7 7.6-6 7.6s-6-3.2-6-7.6c0-3.8 2.2-8 6-11.4Z",
            "M10 10h.01",
            "M14 10h.01",
            "M12 13h.01",
            "M10 16h.01",
            "M14 16h.01",
        )
    }

    val Olive: ImageVector by lazy {
        lucideGlyph(
            "GlyphOlive",
            "M3 20.5 21 4",
            "M6 17.3c-2.6-.4-3.8-2.3-3.6-5 2.5.3 3.8 2.2 3.6 5Z",
            "M12 11.9c-.6-2.6.4-4.8 3-6 .6 2.6-.4 4.8-3 6Z",
            "M9.5 14.3l2.2 1.2",
            "M12.5 15.3c1.3 0 2.3 1.3 2.3 3s-1 3-2.3 3-2.3-1.3-2.3-3 1-3 2.3-3Z",
            "M15.5 8.8l2.2 1.1",
            "M18.5 9.8c1.3 0 2.3 1.3 2.3 3s-1 3-2.3 3-2.3-1.3-2.3-3 1-3 2.3-3Z",
        )
    }

    val Kaki: ImageVector by lazy {
        lucideGlyph(
            "GlyphKaki",
            "M12 21c-4.7 0-8-2.8-8-6.6 0-3.6 3.3-6.4 8-6.4s8 2.8 8 6.4c0 3.8-3.3 6.6-8 6.6Z",
            "M5.5 9.5c2 .2 3.6-.6 4.4-2.4.7.8 1.4 1.2 2.1 1.2s1.4-.4 2.1-1.2c.8 1.8 2.4 2.6 4.4 2.4",
            "M12 8.2V4.5",
        )
    }

    val Grenade: ImageVector by lazy {
        lucideGlyph(
            "GlyphGrenade",
            circlePath(12f, 14.5f, 7f),
            "M9.5 8 9 4.2l2 1.4 1-2.1 1 2.1 2-1.4-.5 3.8",
        )
    }

    val Arbouse: ImageVector by lazy {
        lucideGlyph(
            "GlyphArbouse",
            circlePath(12f, 14f, 7f),
            "M12 7V4",
            "M9 11.5h.01",
            "M13 11h.01",
            "M15.5 14h.01",
            "M11 14.5h.01",
            "M8 16h.01",
            "M13 17.5h.01",
        )
    }

    val Framboise: ImageVector by lazy {
        lucideGlyph(
            "GlyphFramboise",
            "M12 21.5c-3.9 0-6.5-3.4-6.5-7 0-2.6 1.6-4.5 3.5-4.5h6c1.9 0 3.5 1.9 3.5 4.5 0 3.6-2.6 7-6.5 7Z",
            "M9 10 6 6.5",
            "M12 10V5",
            "M15 10l3-3.5",
            "M9.5 13.5h.01",
            "M14.5 13.5h.01",
            "M12 16h.01",
            "M9.5 18.5h.01",
            "M14.5 18.5h.01",
        )
    }

    val Groseille: ImageVector by lazy {
        lucideGlyph(
            "GlyphGroseille",
            "M5 2.5c2.5.5 4 2 4.5 3",
            circlePath(9.5f, 8f, 2.5f),
            circlePath(12.5f, 13f, 2.5f),
            circlePath(16f, 17.8f, 2.5f),
        )
    }

    val Myrtille: ImageVector by lazy {
        lucideGlyph(
            "GlyphMyrtille",
            circlePath(12f, 13.5f, 7.5f),
            "M10.3 10.6l1.7 1.6 1.7-1.6",
            "M12 12.2V9.6",
        )
    }

    val Cynorhodon: ImageVector by lazy {
        lucideGlyph(
            "GlyphCynorhodon",
            "M12 22c-2.8 0-4.5-3-4.5-6.8S9.2 8 12 8s4.5 3.4 4.5 7.2S14.8 22 12 22Z",
            "M12 8 8.5 3",
            "M12 8l3.5-5",
            "M12 8V2.5",
        )
    }

    val Fraise: ImageVector by lazy {
        lucideGlyph(
            "GlyphFraise",
            "M12 21.5c-3.5-1.5-7-5.5-7-9.5 0-2.2 1.6-3.5 3.5-3.5 1.4 0 2.6.6 3.5 1.4.9-.8 2.1-1.4 3.5-1.4 1.9 0 " +
                "3.5 1.3 3.5 3.5 0 4-3.5 8-7 9.5Z",
            "M8 8.6 9.5 6 12 7.5 14.5 6 16 8.6",
            "M12 7.5V3.5",
            "M9.8 12.5h.01",
            "M14.2 12.5h.01",
            "M12 15.8h.01",
        )
    }

    val Brin: ImageVector by lazy {
        lucideGlyph(
            "GlyphBrin",
            "M12 22V3",
            "M12 7 9 4.5",
            "M12 7l3-2.5",
            "M12 12 8 9",
            "M12 12l4-3",
            "M12 17l-4.5-3",
            "M12 17l4.5-3",
        )
    }

    val Radis: ImageVector by lazy {
        lucideGlyph(
            "GlyphRadis",
            circlePath(12f, 15f, 5.5f),
            "M12 20.5V23",
            "M12 9.5c-1-3-3.4-5-6.5-5 .3 3 2.8 5 6.5 5Z",
            "M12 9.5c1-3 3.4-5 6.5-5-.3 3-2.8 5-6.5 5Z",
        )
    }

    val Courge: ImageVector by lazy {
        lucideGlyph(
            "GlyphCourge",
            "M12 8c-5 0-9 2.9-9 6.5S7 21 12 21s9-2.9 9-6.5S17 8 12 8Z",
            "M12 8c-2.6 1.6-2.6 11.4 0 13",
            "M12 8c2.6 1.6 2.6 11.4 0 13",
            "M12 8c0-2 .6-3.4 2-4.5",
        )
    }

    val Salade: ImageVector by lazy {
        lucideGlyph(
            "GlyphSalade",
            "M12 21c-4.4 0-8-2.5-8-6.5 0-1.5.7-2.6 1.8-3.3-.2-2.4 1.6-4.4 4-4.2.8-1.2 1.5-1.5 2.2-1.5s1.4.3 2.2 " +
                "1.5c2.4-.2 4.2 1.8 4 4.2 1.1.7 1.8 1.8 1.8 3.3 0 4-3.6 6.5-8 6.5Z",
            "M12 21v-8",
            "M12 14 9 11",
            "M12 16l3-3",
        )
    }

    val Gousse: ImageVector by lazy {
        lucideGlyph(
            "GlyphGousse",
            "M4.5 19.5C10 18 17.5 12 19 4.5c1.5 1 1.8 3 1 5C18 15 12 20.5 6 21c-1 0-1.6-.8-1.5-1.5Z",
            "M9 17.5h.01",
            "M13 15h.01",
            "M16.5 11h.01",
        )
    }

    val Fruit: ImageVector by lazy {
        lucideGlyph(
            "GlyphFruit",
            "M21.66 17.67a1.08 1.08 0 0 1-.04 1.6A12 12 0 0 1 4.73 2.38a1.1 1.1 0 0 1 1.61-.04z",
            "M19.65 15.66A8 8 0 0 1 8.35 4.34",
            "m14 10-5.5 5.5",
        )
    }

    val Fleur: ImageVector by lazy {
        lucideGlyph(
            "GlyphFleur",
            "M12 16.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 1 1 12 7.5a4.5 4.5 0 1 1 4.5 4.5 4.5 4.5 0 " +
                "1 1-4.5 4.5",
            "M12 7.5V9",
            "M7.5 12H9",
            "M16.5 12H15",
            "M12 16.5V15",
            "m8 8 1.88 1.88",
            "M14.12 9.88 16 8",
            "m8 16 1.88-1.88",
            "M14.12 14.12 16 16",
        )
    }

    val Feuille: ImageVector by lazy {
        lucideGlyph(
            "GlyphFeuille",
            "M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z",
            "M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12",
        )
    }

    /** Meme dessin que [Noix] : « coque » est le repli de la partie recoltee, pas une autre plante. */
    val Coque: ImageVector by lazy { nutGlyph("GlyphCoque") }

    val Racine: ImageVector by lazy {
        lucideGlyph(
            "GlyphRacine",
            "M2.27 21.7s9.87-3.5 12.73-6.36a4.5 4.5 0 0 0-6.36-6.37C5.77 11.84 2.27 21.7 2.27 " +
                "21.7zM8.64 14l-2.05-2.04M15.34 15l-2.46-2.46",
            "M22 9s-1.33-2-3.5-2C16.86 7 15 9 15 9s1.33 2 3.5 2S22 9 22 9z",
            "M15 2s-2 1.33-2 3.5S15 9 15 9s2-1.84 2-3.5C17 3.33 15 2 15 2z",
        )
    }

    /** Lucide `tree-deciduous`. */
    val Arbre: ImageVector by lazy {
        lucideGlyph(
            "GlyphArbre",
            "M8 19a4 4 0 0 1-2.24-7.32A3.5 3.5 0 0 1 9 6.03V6a3 3 0 1 1 6 0v.04a3.5 3.5 0 0 1 " +
                "3.24 5.65A4 4 0 0 1 16 19Z",
            "M12 19v3",
        )
    }

    /** Lucide `lock`, le rectangle arrondi converti en path. */
    val Cadenas: ImageVector by lazy {
        lucideGlyph(
            "GlyphCadenas",
            "M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z",
            "M7 11V7a5 5 0 0 1 10 0v4",
        )
    }

    private fun nutGlyph(name: String): ImageVector =
        lucideGlyph(
            name,
            "M12 4V2",
            "M5 10v4a7.004 7.004 0 0 0 5.277 6.787c.412.104.802.292 1.102.592L12 22l.621-.621c.3-" +
                ".3.69-.488 1.102-.592A7.003 7.003 0 0 0 19 14v-4",
            "M12 4C8 4 4.5 6 4 8c-.243.97-.919 1.952-2 3 1.31-.082 1.972-.29 3-1 .54.92.982 " +
                "1.356 2 2 1.452-.647 1.954-1.098 2.5-2 .595.995 1.151 1.427 2.5 2 1.31-.621 " +
                "1.862-1.058 2.5-2 .629.977 1.162 1.423 2.5 2 1.209-.548 1.68-.967 2-2 1.032.916 " +
                "1.683 1.157 3 1-1.297-1.036-1.758-2.03-2-3-.5-2-4-4-8-4Z",
        )

    /**
     * Map key -> glyph, stable for the lifetime of the process (backed by `by lazy`
     * vals on a singleton `object`). See [rememberSpeciesGlyphPainters] for why that
     * stability matters.
     */
    val all: Map<String, ImageVector> by lazy {
        mapOf(
            KEY_POMME to Pomme,
            KEY_POIRE to Poire,
            KEY_COING to Coing,
            KEY_NEFLE to Nefle,
            KEY_SORBE to Sorbe,
            KEY_NEFLE_JAPON to NefleJapon,
            KEY_CERISE to Cerise,
            KEY_PECHE to Peche,
            KEY_ABRICOT to Abricot,
            KEY_PRUNE to Prune,
            KEY_PETITES_PRUNES to PetitesPrunes,
            KEY_PRUNELLE to Prunelle,
            KEY_AMANDE to Amande,
            KEY_FIGUE to Figue,
            KEY_OLIVE to Olive,
            KEY_KAKI to Kaki,
            KEY_GRENADE to Grenade,
            KEY_ARBOUSE to Arbouse,
            KEY_NOIX to Noix,
            KEY_NOISETTE to Noisette,
            KEY_CHATAIGNE to Chataigne,
            KEY_MURE to Mure,
            KEY_FRAMBOISE to Framboise,
            KEY_GROSEILLE to Groseille,
            KEY_GRAPPE_BAIES to GrappeBaies,
            KEY_MYRTILLE to Myrtille,
            KEY_CYNORHODON to Cynorhodon,
            KEY_OMBELLE to Ombelle,
            KEY_RAISIN to Raisin,
            KEY_FRAISE to Fraise,
            KEY_BRIN to Brin,
            KEY_RADIS to Radis,
            KEY_COURGE to Courge,
            KEY_SALADE to Salade,
            KEY_GOUSSE to Gousse,
            KEY_FRUIT to Fruit,
            KEY_FLEUR to Fleur,
            KEY_FEUILLE to Feuille,
            KEY_COQUE to Coque,
            KEY_RACINE to Racine,
            KEY_ARBRE to Arbre,
            KEY_CADENAS to Cadenas,
        )
    }
}

/**
 * Remembers a [Painter] per species glyph key, with a **stable Map identity** across
 * recompositions.
 *
 * [FruxSpeciesGlyphs.all]'s entries never change at runtime (fixed at compile time on a
 * singleton `object`), so `rememberVectorPainter` — itself remembered by call site/key
 * inside the `mapValues` lambda — always returns the same [Painter] instance for a given
 * glyph after the first composition. Capturing the resulting Map once in `remember {}`
 * therefore freezes its identity for the composable's lifetime without losing any of
 * those per-painter caches. The alternative (returning a fresh Map every recomposition)
 * would look like "new" painters to MapLibre's `ImageManager`, forcing it to
 * re-register every marker sprite on every frame — a real perf hit given the map's
 * camera recomposes continuously (ADR 0009).
 */
@Composable
fun rememberSpeciesGlyphPainters(): Map<String, Painter> {
    val painters = FruxSpeciesGlyphs.all.mapValues { (_, glyph) -> rememberVectorPainter(glyph) }
    return remember { painters }
}
